/**
 * CL-408B / CL-408H / CL-ENGINEER-LAUNCH-001 — Planning / project photos selection.
 */

import { enrichProjectLibraryFile } from "../projectLibrary/projectLibraryEngine";
import {
  isProjectPhotoPortalVisible,
  resolveProjectPhotoPortalVisibility,
} from "./projectPhotoPortalVisibilityModel";

/** Future-ready planning metadata keys on project files (not written yet). */
export const PLANNING_PHOTO_FUTURE_METADATA_KEYS = [
  "planningCategory",
  "relatedScopeAreaId",
  "relatedScopeItemId",
];

function safeStr(value) {
  return String(value ?? "").trim();
}

export function enrichPlanningPhotoCandidate(file = {}) {
  if (!file || typeof file !== "object") return file;
  return enrichProjectLibraryFile(file);
}

function isLikelyImage(fileMeta) {
  const t = safeStr(fileMeta?.contentType || fileMeta?.type || fileMeta?.mimeType || "").toLowerCase();
  if (t.startsWith("image/")) return true;
  const name = safeStr(fileMeta?.name || "").toLowerCase();
  return /\.(png|jpg|jpeg|webp|gif|heic)$/i.test(name);
}

function timestampMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function isPlanningProjectPhotoFile(file = {}, enriched = null) {
  const row = enriched || enrichPlanningPhotoCandidate(file);
  const isDeleted = safeStr(row?.status || "active").toLowerCase() === "deleted";
  if (isDeleted) return false;

  const libraryType = safeStr(row?.libraryType || "").toLowerCase();
  const category = safeStr(row?.category || "").toLowerCase();
  const subcategory = safeStr(row?.subcategory || "").toLowerCase();

  if (libraryType === "photo") return true;
  if (category === "photos" || subcategory === "photos") {
    return isLikelyImage(row);
  }
  if (category.includes("photo") || subcategory.includes("photo")) {
    return isLikelyImage(row);
  }
  return false;
}

/** @deprecated Prefer isProjectPhotoPortalVisible — same rule for all external portal surfaces. */
export function isClientVisiblePlanningPhoto(file = {}) {
  return isProjectPhotoPortalVisible(file);
}

/**
 * Options for planning photo selection.
 * - adminAudience: include portal-hidden photos (admin management).
 * - clientAudience / accessPreFiltered: retained for callers; portal visibility always applies unless adminAudience.
 */
export const CLIENT_PLANNING_PHOTO_OPTIONS = Object.freeze({
  clientAudience: true,
});

export const SHARED_FILES_PLANNING_PHOTO_OPTIONS = Object.freeze({
  clientAudience: true,
  accessPreFiltered: true,
});

/** Collaborators see portal-visible photos only (same rule as clients). */
export const COLLABORATOR_PLANNING_PHOTO_OPTIONS = Object.freeze({
  clientAudience: true,
});

export const ADMIN_PLANNING_PHOTO_OPTIONS = Object.freeze({
  adminAudience: true,
});

export function selectPlanningProjectPhotos(files = [], options = {}) {
  const adminAudience = options?.adminAudience === true;

  return (Array.isArray(files) ? files : [])
    .map((file) => {
      const enriched = enrichPlanningPhotoCandidate(file);
      return { file, enriched };
    })
    .filter(({ file, enriched }) => isPlanningProjectPhotoFile(file, enriched))
    .filter(({ file }) => {
      if (adminAudience) return true;
      // CL-ENGINEER-LAUNCH-001: never let canViewPhotos / accessPreFiltered override hidden.
      return isProjectPhotoPortalVisible(file);
    })
    .map(({ file, enriched }) => ({
      ...file,
      ...enriched,
      _previewUrl: safeStr(
        file.thumbnailUrl || file.thumbUrl || file.previewUrl || file.url || enriched.thumbnailUrl || enriched.url
      ),
      _updatedMs: timestampMillis(file.updatedAt || file.createdAt),
      _portalVisibility: resolveProjectPhotoPortalVisibility(file),
    }))
    .sort((a, b) => (b._updatedMs || 0) - (a._updatedMs || 0));
}

export function buildPlanningProjectPhotosSummary(files = [], options = {}) {
  const photos = selectPlanningProjectPhotos(files, options);
  const lastUpdatedMs = photos.reduce((max, row) => Math.max(max, row._updatedMs || 0), 0);

  return {
    photoCount: photos.length,
    hasPhotos: photos.length > 0,
    previewPhotos: photos.slice(0, 8),
    lastUpdatedMs,
    lastUpdatedLabel: lastUpdatedMs ? formatPhotoTimestamp(lastUpdatedMs) : null,
  };
}

export function buildPlanningPhotosNextRecommendation(photoCount = 0) {
  if (!photoCount) {
    return "Upload field photos so estimators can see existing conditions";
  }
  if (photoCount < 5) {
    return "Add more photos of key rooms, deficiencies, and site context";
  }
  return "Review photos and link them to scope areas when tagging is available";
}

function formatPhotoTimestamp(ms) {
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

export {
  isProjectPhotoPortalVisible,
  resolveProjectPhotoPortalVisibility,
};
